import axios from "axios";

import { fetchSession } from "@/AuthContext";
import { normalizeRoute } from "@/utils/route";
import * as Sentry from "@sentry/react";
import { Amplify } from "@aws-amplify/core";

Amplify.configure({
  Auth: {
    Cognito: {
      region: import.meta.env.VITE_APP_COGNITO_REGION,
      userPoolId: import.meta.env.VITE_APP_COGNITO_USERPOOLID,
      userPoolClientId: import.meta.env.VITE_APP_COGNITO_USERPOOLWEBCLIENTID,
    },
  },
});

export default class Api {
  constructor() {
    this.axios = axios.create({
      baseURL: import.meta.env.VITE_APP_API_BASE,
      timeout: 30000,
      headers: {
        common: {
          "Content-Type": "application/json",
          "x-app-version": import.meta.env.VITE_APP_VERSION,
        },
      },
    });

    this.axios.interceptors.request.use(
      (config) => this.getUserToken(config),
      (error) => {
        return Promise.reject(error);
      }
    );

    this.axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        if (status === 422) {
          console.log("422 REFRESH");
        }
        // Skip transient client-side failures (timeouts, dropped connections,
        // user-cancelled requests). They surface as no-status axios errors but
        // aren't actionable on the server side. WEBAPP-21.
        const transientCodes = new Set(["ECONNABORTED", "ETIMEDOUT", "ERR_NETWORK", "ERR_CANCELED"]);
        if (!status && transientCodes.has(error?.code)) {
          return Promise.reject(error);
        }
        // Report server errors and remaining network failures to Sentry.
        // Skip expected client errors (auth, validation, not found).
        if (!status || status >= 500) {
          const method = (error?.config?.method || "GET").toUpperCase();
          const url = error?.config?.url || "";
          const route = normalizeRoute(url);
          const statusKey = status ? String(status) : "network";
          // Group by route + method + status so each endpoint gets its own
          // issue instead of every 5xx collapsing into a single axios bucket.
          Sentry.captureException(error, {
            level: status === 503 ? "warning" : "error",
            tags: { source: "api", "api.route": route, "api.method": method, "api.status": statusKey },
            fingerprint: ["api-error", method, route, statusKey],
            contexts: {
              request: {
                method,
                url,
                route,
                status,
              },
            },
          });
        }
        return Promise.reject(error);
      }
    );
  }

  async getUserToken(config) {
    try {
      const { accessToken, idToken } = (await fetchSession()).tokens ?? {};
      config.headers["Authorization"] = `Bearer ${idToken}`;
    } catch (e) {
      // console.error("getUserToken", e);
    }
    return config;
  }
}
